import React, { useEffect } from "react"
import { Skeleton, styled } from "@mui/material"

import { colors } from "theme"
import { useAppDispatch, useAppSelector } from "redux/hooks"
import { fetchDebts } from "redux/features/debt/debtThunk"
import {
  selectFetchCollectionStatus,
  selectTotal,
  selectAll,
  selectUnsolvedDebts,
  selectDraftSentToSelfServiceDebts,
  selectDraftPaymentScheduleDebts,
  selectPaymentScheduleDebts,
  selectOverdueDebts,
  selectCourtPreparingDebts,
  selectCourtDebts,
  selectBailiffDebts,
  selectPaidDebts,
  selectEndedDebts,
} from "redux/features/debt/debtSlice"

import LoadableCollection from "components/ui/LoadableCollection"
import { DebtListType } from "types"

export function withDebts<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithDebts = (props: GenericType) => {
    const dispatch = useAppDispatch()

    const items = useAppSelector((state) => selectAll(state))
    const total = useAppSelector((state) => selectTotal(state))
    const fetchStatus = useAppSelector((state) =>
      selectFetchCollectionStatus(state),
    )

    const unsolved = useAppSelector((state) =>
      selectUnsolvedDebts(state),
    ) as DebtListType
    const draft_sent_to_self_service = useAppSelector((state) =>
      selectDraftSentToSelfServiceDebts(state),
    ) as DebtListType
    const draft = useAppSelector((state) =>
      selectDraftPaymentScheduleDebts(state),
    ) as DebtListType
    const active = useAppSelector((state) =>
      selectPaymentScheduleDebts(state),
    ) as DebtListType
    const overdue = useAppSelector((state) =>
      selectOverdueDebts(state),
    ) as DebtListType
    const court_preparing = useAppSelector((state) =>
      selectCourtPreparingDebts(state),
    ) as DebtListType
    const court = useAppSelector((state) =>
      selectCourtDebts(state),
    ) as DebtListType
    const bailiff = useAppSelector((state) =>
      selectBailiffDebts(state),
    ) as DebtListType
    const paid = useAppSelector((state) =>
      selectPaidDebts(state),
    ) as DebtListType
    const ended = useAppSelector((state) =>
      selectEndedDebts(state),
    ) as DebtListType

    const debts = {
      unsolved,
      draft_sent_to_self_service,
      draft,
      active,
      overdue,
      court_preparing,
      court,
      bailiff,
      paid,
      ended,
      items,
      total,
    }

    const handleFetchDebts = () => {
      if (!fetchStatus.isFetching) {
        dispatch(fetchDebts())
      }
    }

    useEffect(() => handleFetchDebts(), [])

    const renderSkeleton = () => {
      return (
        <>
          <Card sx={{ height: 360 }} animation="pulse" variant="rectangular" />
          <Card sx={{ height: 360 }} animation="pulse" variant="rectangular" />
          <Card sx={{ height: 360 }} animation="pulse" variant="rectangular" />
        </>
      )
    }

    return (
      <LoadableCollection
        fetchStatus={fetchStatus}
        renderLoading={renderSkeleton}
      >
        {() => (
          <WrappedComponent
            {...props}
            debts={debts}
            fetchStatus={fetchStatus}
            fetchDebts={handleFetchDebts}
          />
        )}
      </LoadableCollection>
    )
  }

  return WithDebts
}

const Card = styled(Skeleton)`
  background-color: ${colors.cultured};
  box-shadow: 12px 14px 23px -2px rgb(0 0 0 / 3%);
  border-radius: 26px;
  margin-bottom: 16px;
  padding: 58px;
`
